import { z } from "zod";

import { jurisdictionProfileSchema } from "../services/cableContract";
import {
  conductorMaterialSchema,
  conductorRoleSchema,
  ELECTRODE_DIMENSIONS,
  electrodeKindSchema,
  K_TABLE_COLUMNS,
  kTableIdSchema,
  systemKindSchema,
} from "../services/earthingContract";
import {
  EARTHING_LABELS,
  ELECTRODE_KIND_LABELS,
  K_COLUMN_LABELS,
  K_TABLE_LABELS,
  MATERIAL_LABELS,
  ROLE_LABELS,
  SYSTEM_KIND_LABELS,
} from "./earthingLabels";

// Draft model of the earthing form (EOS-08a, A36): what the user is typing, kept apart from
// the components so the payload rules are tested without a browser.
//
// Every value is held as the string the user typed. A conductor row takes its figures from one
// stored run of the active revision (LINKED, the default) or from declarations alone (NONE): a
// circuit protective conductor links one row of an EOS-05d earth-fault loop run (t, I, S and
// the material, D5), a board conductor links an EOS-04 earth-fault run (I, D3); a declared
// figure always governs. k comes from a held table case (TABLE: IS 3043 Table 11A to 11D or
// CPWD App F Table F-6 / F-7, the register's value), from a declared figure with its source
// (DECLARED), or is left not established (NONE). A blank optional item (a source, a declared
// figure, the soil's test record, the target, an electrode's factor) is left OUT of the
// payload: blank means NOT ESTABLISHED, and the engine must see it as absent, never as "". A
// required figure is sent as "" so the schema names it by its on-screen label; a choice the
// user has not made ("" = Choose...) is left out so the schema reports it as required.
//
// Conductor rows and electrodes are separate entries with stable ids, so adding or removing
// one never disturbs the others; the ids never reach the backend.

/** The module key the draft is kept under in the browser tab (see useStudyDraft). */
export const EARTHING_DRAFT_MODULE = "earthing";

/** Where a conductor row's figures come from, on screen. */
export const LINK_SOURCES = ["LINKED", "NONE"] as const;
export type LinkSourceChoice = (typeof LINK_SOURCES)[number];

/** Where k comes from, on screen. */
export const K_SOURCES = ["TABLE", "DECLARED", "NONE"] as const;
export type KSourceChoice = (typeof K_SOURCES)[number];

export type ConductorRowDraft = {
  id: string;
  code: string;
  name: string;
  role: string;
  linkSource: LinkSourceChoice;
  faultRunId: string;
  disconnectionRunId: string;
  disconnectionRowCode: string;
  faultCurrentA: string;
  faultCurrentSource: string;
  letThroughEnergyA2s: string;
  letThroughEnergySource: string;
  disconnectionTimeS: string;
  disconnectionTimeSource: string;
  areaMm2: string;
  areaSource: string;
  conductorMaterial: string;
  kSource: KSourceChoice;
  kTable: string;
  kColumn: string;
  kValue: string;
  kValueSource: string;
  sldElementCode: string;
  notes: string;
};

export type ElectrodeDraft = {
  id: string;
  code: string;
  kind: string;
  rodLengthM: string;
  rodDiameterMm: string;
  plateLengthM: string;
  plateWidthM: string;
  stripLengthM: string;
  stripWidthMm: string;
  conductorLengthM: string;
  conductorDiameterMm: string;
  dimensionSource: string;
  material: string;
  countInParallel: string;
  parallelFactor: string;
  parallelFactorSource: string;
  spacingM: string;
  notes: string;
};

export type EarthingDraft = {
  code: string;
  name: string;
  busCode: string;
  systemKind: string;
  systemVoltageV: string;
  systemVoltageSource: string;
  conductors: ConductorRowDraft[];
  electrodes: ElectrodeDraft[];
  soilResistivityOhmM: string;
  soilTestMethod: string;
  soilTestDate: string;
  soilSource: string;
  seasonalFactor: string;
  seasonalFactorSource: string;
  targetResistanceOhm: string;
  targetResistanceSource: string;
  jurisdictionProfile: string;
  notes: string;
};

// A choice the user has not made yet is held as "".
function chosenOrBlank<T extends z.ZodType<string>>(choice: T) {
  return z.union([z.literal(""), choice]);
}

const conductorRowDraftSchema = z
  .object({
    id: z.string(),
    code: z.string(),
    name: z.string(),
    role: chosenOrBlank(conductorRoleSchema),
    linkSource: z.enum(LINK_SOURCES),
    faultRunId: z.string(),
    disconnectionRunId: z.string(),
    disconnectionRowCode: z.string(),
    faultCurrentA: z.string(),
    faultCurrentSource: z.string(),
    letThroughEnergyA2s: z.string(),
    letThroughEnergySource: z.string(),
    disconnectionTimeS: z.string(),
    disconnectionTimeSource: z.string(),
    areaMm2: z.string(),
    areaSource: z.string(),
    conductorMaterial: chosenOrBlank(conductorMaterialSchema),
    kSource: z.enum(K_SOURCES),
    kTable: chosenOrBlank(kTableIdSchema),
    kColumn: z.string(),
    kValue: z.string(),
    kValueSource: z.string(),
    sldElementCode: z.string(),
    notes: z.string(),
  })
  .strict();

const electrodeDraftSchema = z
  .object({
    id: z.string(),
    code: z.string(),
    kind: chosenOrBlank(electrodeKindSchema),
    rodLengthM: z.string(),
    rodDiameterMm: z.string(),
    plateLengthM: z.string(),
    plateWidthM: z.string(),
    stripLengthM: z.string(),
    stripWidthMm: z.string(),
    conductorLengthM: z.string(),
    conductorDiameterMm: z.string(),
    dimensionSource: z.string(),
    material: z.string(),
    countInParallel: z.string(),
    parallelFactor: z.string(),
    parallelFactorSource: z.string(),
    spacingM: z.string(),
    notes: z.string(),
  })
  .strict();

// The shape a draft kept in the browser tab must still have to be restored: exactly the fields
// above. A draft saved by an older form fails here and is dropped instead of half-filling this one.
export const earthingDraftSchema = z
  .object({
    code: z.string(),
    name: z.string(),
    busCode: z.string(),
    systemKind: chosenOrBlank(systemKindSchema),
    systemVoltageV: z.string(),
    systemVoltageSource: z.string(),
    conductors: z.array(conductorRowDraftSchema),
    electrodes: z.array(electrodeDraftSchema),
    soilResistivityOhmM: z.string(),
    soilTestMethod: z.string(),
    soilTestDate: z.string(),
    soilSource: z.string(),
    seasonalFactor: z.string(),
    seasonalFactorSource: z.string(),
    targetResistanceOhm: z.string(),
    targetResistanceSource: z.string(),
    jurisdictionProfile: jurisdictionProfileSchema,
    notes: z.string(),
  })
  .strict();

let nextConductorNumber = 1;
let nextElectrodeNumber = 1;

// Prefixes of their own, so a row id is never mistaken for another form's.
export function createConductorRowId(): string {
  const id = `earth-conductor-${nextConductorNumber}`;
  nextConductorNumber += 1;
  return id;
}

export function createElectrodeId(): string {
  const id = `earth-electrode-${nextElectrodeNumber}`;
  nextElectrodeNumber += 1;
  return id;
}

// Row ids come from counters that start again at 1 on every page load, while a restored draft
// brings the ids it was saved with. Moving the counters past every id in use keeps a row added
// afterwards from taking the id of one already on screen.
export function seedEarthingIdCounters(ids: readonly string[]): void {
  for (const id of ids) {
    const conductor = /^earth-conductor-(\d+)$/.exec(id);
    if (conductor !== null) {
      nextConductorNumber = Math.max(nextConductorNumber, Number(conductor[1]) + 1);
    }
    const electrode = /^earth-electrode-(\d+)$/.exec(id);
    if (electrode !== null) {
      nextElectrodeNumber = Math.max(nextElectrodeNumber, Number(electrode[1]) + 1);
    }
  }
}

export function createConductorRowDraft(): ConductorRowDraft {
  return {
    id: createConductorRowId(),
    code: "",
    name: "",
    role: "",
    linkSource: "LINKED",
    faultRunId: "",
    disconnectionRunId: "",
    disconnectionRowCode: "",
    faultCurrentA: "",
    faultCurrentSource: "",
    letThroughEnergyA2s: "",
    letThroughEnergySource: "",
    disconnectionTimeS: "",
    disconnectionTimeSource: "",
    areaMm2: "",
    areaSource: "",
    conductorMaterial: "",
    kSource: "TABLE",
    kTable: "",
    kColumn: "",
    kValue: "",
    kValueSource: "",
    sldElementCode: "",
    notes: "",
  };
}

export function createElectrodeDraft(): ElectrodeDraft {
  return {
    id: createElectrodeId(),
    code: "",
    kind: "",
    rodLengthM: "",
    rodDiameterMm: "",
    plateLengthM: "",
    plateWidthM: "",
    stripLengthM: "",
    stripWidthMm: "",
    conductorLengthM: "",
    conductorDiameterMm: "",
    dimensionSource: "",
    material: "",
    countInParallel: "",
    parallelFactor: "",
    parallelFactorSource: "",
    spacingM: "",
    notes: "",
  };
}

// Only the defaults the backend itself has are filled in: the IN profile. Every choice starts
// blank; one empty conductor row and one empty electrode wait for the engineer; a row's figures
// on a linked run, its k on a held table case.
export function createInitialEarthingDraft(): EarthingDraft {
  return {
    code: "",
    name: "",
    busCode: "",
    systemKind: "",
    systemVoltageV: "",
    systemVoltageSource: "",
    conductors: [createConductorRowDraft()],
    electrodes: [createElectrodeDraft()],
    soilResistivityOhmM: "",
    soilTestMethod: "",
    soilTestDate: "",
    soilSource: "",
    seasonalFactor: "",
    seasonalFactorSource: "",
    targetResistanceOhm: "",
    targetResistanceSource: "",
    jurisdictionProfile: "IN",
    notes: "",
  };
}

// An optional text or decimal field: sent trimmed, or left out when blank.
function optional(key: string, typed: string): Record<string, string> {
  const value = typed.trim();
  return value === "" ? {} : { [key]: value };
}

// A declared figure with its source, required: always sent (a blank value as "" so the schema
// names it by its label), the source only when typed.
function required(key: string, figure: string, source: string): Record<string, unknown> {
  return { [key]: { value: figure.trim(), ...optional("source", source) } };
}

// A declared figure with its source, optional: sent when its figure is typed, left out when blank.
function declared(key: string, figure: string, source: string): Record<string, unknown> {
  const value = figure.trim();
  return value === "" ? {} : { [key]: { value, ...optional("source", source) } };
}

/** The dimension draft fields of each electrode kind, in the request's field names. */
export const ELECTRODE_DIMENSION_FIELDS: Record<string, keyof ElectrodeDraft> = {
  rod_length_m: "rodLengthM",
  rod_diameter_mm: "rodDiameterMm",
  plate_length_m: "plateLengthM",
  plate_width_m: "plateWidthM",
  strip_length_m: "stripLengthM",
  strip_width_mm: "stripWidthMm",
  conductor_length_m: "conductorLengthM",
  conductor_diameter_mm: "conductorDiameterMm",
};

/** Whether a role is a conductor at the board (its current from an EOS-04 earth-fault run, D3). */
export function isBoardRole(role: string): boolean {
  return role === "MAIN_PROTECTIVE" || role === "EARTHING_CONDUCTOR";
}

// The row's link as chosen on screen: the earth-fault loop run and its row for a circuit PE,
// the earth-fault run for a board conductor (a blank id is sent as "" so the schema names it),
// or nothing (declared figures only).
function linkPayload(row: ConductorRowDraft): Record<string, unknown> {
  if (row.linkSource !== "LINKED" || row.role === "") {
    return {};
  }
  if (isBoardRole(row.role)) {
    return { fault_run_id: row.faultRunId.trim() };
  }
  return {
    disconnection_run_id: row.disconnectionRunId.trim(),
    disconnection_row_code: row.disconnectionRowCode.trim(),
  };
}

// k as chosen on screen: the held table case (the table left out when unchosen, so the schema
// names it), the declared figure with its source, or nothing (NOT ESTABLISHED).
function kPayload(row: ConductorRowDraft): Record<string, unknown> {
  if (row.kSource === "TABLE") {
    return { k_table_case: { ...optional("table", row.kTable), column: row.kColumn.trim() } };
  }
  if (row.kSource === "DECLARED") {
    return required("declared_k", row.kValue, row.kValueSource);
  }
  return {};
}

// One conductor row as the request carries it.
function conductorPayload(row: ConductorRowDraft): Record<string, unknown> {
  return {
    code: row.code.trim(),
    name: row.name.trim(),
    ...optional("role", row.role),
    ...linkPayload(row),
    ...declared("declared_fault_current_a", row.faultCurrentA, row.faultCurrentSource),
    ...declared("declared_let_through_energy_a2s", row.letThroughEnergyA2s, row.letThroughEnergySource),
    ...declared("declared_disconnection_time_s", row.disconnectionTimeS, row.disconnectionTimeSource),
    ...declared("declared_area_mm2", row.areaMm2, row.areaSource),
    ...optional("conductor_material", row.conductorMaterial),
    ...kPayload(row),
    ...optional("sld_element_code", row.sldElementCode),
    ...optional("notes", row.notes),
  };
}

// The number of electrodes in parallel: a whole number when typed as one, the typed text
// otherwise (so the schema names the field), nothing when blank (one electrode).
function countPayload(typed: string): Record<string, unknown> {
  const value = typed.trim();
  if (value === "") {
    return {};
  }
  return { count_in_parallel: /^\d+$/.test(value) ? Number(value) : value };
}

// One electrode as the request carries it: the kind's own dimension fields alone.
function electrodePayload(electrode: ElectrodeDraft): Record<string, unknown> {
  const dimensions: Record<string, string> = {};
  const names: readonly string[] =
    electrode.kind in ELECTRODE_DIMENSIONS
      ? ELECTRODE_DIMENSIONS[electrode.kind as keyof typeof ELECTRODE_DIMENSIONS]
      : [];
  for (const name of names) {
    const field = ELECTRODE_DIMENSION_FIELDS[name];
    if (field !== undefined) {
      dimensions[name] = electrode[field].trim();
    }
  }
  return {
    code: electrode.code.trim(),
    ...optional("kind", electrode.kind),
    ...dimensions,
    ...optional("dimension_source", electrode.dimensionSource),
    ...optional("material", electrode.material),
    ...countPayload(electrode.countInParallel),
    ...declared("parallel_factor", electrode.parallelFactor, electrode.parallelFactorSource),
    ...optional("spacing_m", electrode.spacingM),
    ...optional("notes", electrode.notes),
  };
}

// The soil: sent when its resistivity is typed, with the test record and the seasonal factor
// as typed; left out when blank (NOT ESTABLISHED).
function soilPayload(draft: EarthingDraft): Record<string, unknown> {
  const resistivity = draft.soilResistivityOhmM.trim();
  if (resistivity === "") {
    return {};
  }
  return {
    soil: {
      resistivity_ohm_m: resistivity,
      ...optional("test_method", draft.soilTestMethod),
      ...optional("test_date", draft.soilTestDate),
      ...optional("source", draft.soilSource),
      ...declared("seasonal_factor", draft.seasonalFactor, draft.seasonalFactorSource),
    },
  };
}

// The request body as the form will send it. It is validated afterwards with
// earthingRunCreateRequestSchema, so this function does not judge the values and never
// reorders the rows. The one bus code typed names the declared bus and the diagram's bus.
export function buildEarthingRunPayload(
  draft: EarthingDraft,
  projectRevisionId?: string,
): Record<string, unknown> {
  const busCode = draft.busCode.trim();
  return {
    study: {
      code: draft.code.trim(),
      name: draft.name.trim(),
      bus_code: busCode,
      ...optional("system_kind", draft.systemKind),
      ...required("system_voltage_v", draft.systemVoltageV, draft.systemVoltageSource),
      ...optional("sld_bus_code", busCode),
      // Every row is sent, so the number in a message such as "Conductor 2 — Conductor code
      // is required." always matches the row's position on screen.
      conductors: draft.conductors.map(conductorPayload),
      electrodes: draft.electrodes.map(electrodePayload),
      ...soilPayload(draft),
      ...declared("target_resistance_ohm", draft.targetResistanceOhm, draft.targetResistanceSource),
      ...optional("jurisdiction_profile", draft.jurisdictionProfile),
      ...optional("notes", draft.notes),
    },
    ...(projectRevisionId === undefined ? {} : { project_revision_id: projectRevisionId }),
  };
}

export const LINK_SOURCE_LABELS: Record<LinkSourceChoice, string> = {
  LINKED: "Stored run of this revision",
  NONE: "No stored run (declared figures only)",
};

export const K_SOURCE_LABELS: Record<KSourceChoice, string> = {
  TABLE: "Held table (IS 3043 Table 11A to 11D, CPWD App F Table F-6 / F-7)",
  DECLARED: "Declared k with its source (not verified)",
  NONE: "Not established (the thermal check is not formed)",
};

/** The columns the chosen table prints, labelled; none for an unchosen table. */
export function columnsOf(table: string): readonly string[] {
  return table in K_TABLE_COLUMNS ? K_TABLE_COLUMNS[table as keyof typeof K_TABLE_COLUMNS] : [];
}

export {
  EARTHING_LABELS,
  ELECTRODE_KIND_LABELS,
  K_COLUMN_LABELS,
  K_TABLE_LABELS,
  MATERIAL_LABELS,
  ROLE_LABELS,
  SYSTEM_KIND_LABELS,
};
